import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from 'components/AppIcon';

const forecastData = [
  { month: 'Mar', actual: 4820000, forecast: 4820000, lower: 4820000, upper: 4820000 },
  { month: 'Apr', actual: null, forecast: 5150000, lower: 4900000, upper: 5400000 },
  { month: 'May', actual: null, forecast: 5480000, lower: 5100000, upper: 5860000 },
  { month: 'Jun', actual: null, forecast: 5920000, lower: 5450000, upper: 6390000 },
  { month: 'Jul', actual: null, forecast: 6340000, lower: 5800000, upper: 6880000 },
  { month: 'Aug', actual: null, forecast: 6780000, lower: 6150000, upper: 7410000 },
];

const formatPHP = (v) => `₱${(v / 1000000)?.toFixed(1)}M`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-xs font-caption">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload?.map((p, i) => (
        <p key={i} style={{ color: p?.color }}>{p?.name}: {formatPHP(p?.value)}</p>
      ))}
    </div>
  );
};

export default function ForecastPanel() {
  const [accuracy] = useState(87.4);

  return (
    <div className="card p-4 lg:p-6 chart-panel-depth">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading font-semibold text-lg md:text-xl" style={{ color: '#0F172A', letterSpacing: '0.01em' }}>Sales Forecast</h3>
          <p className="font-caption text-sm mt-0.5" style={{ color: '#475569' }}>Next 5 months AI-powered prediction</p>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
            <Icon name="BrainCircuit" size={14} color="var(--color-success)" strokeWidth={2} />
            <span className="font-data text-sm font-semibold text-emerald-700">{accuracy}%</span>
          </div>
          <span className="font-caption text-xs mt-1" style={{ color: '#475569' }}>Model Accuracy</span>
        </div>
      </div>
      <div className="w-full h-56 md:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={forecastData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="forecastGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#DC2626" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#DC2626" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#059669" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#059669" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" strokeOpacity={0.6} />
            <XAxis dataKey="month" tick={{ fontSize: 13, fontFamily: 'var(--font-caption)', fill: '#374151', fontWeight: 600 }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={formatPHP} tick={{ fontSize: 13, fontFamily: 'var(--font-data)', fill: '#374151', fontWeight: 600 }} axisLine={false} tickLine={false} width={60} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 13, fontFamily: 'var(--font-caption)', color: '#1A1A2E', fontWeight: 600 }} />
            <Area type="monotone" dataKey="actual" name="Actual" stroke="#059669" strokeWidth={2.5} fill="url(#actualGrad)" connectNulls={false} dot={{ r: 5, fill: '#FFFFFF', stroke: '#059669', strokeWidth: 2.5 }} activeDot={{ r: 7 }} />
            <Area type="monotone" dataKey="forecast" name="Forecast" stroke="#DC2626" strokeWidth={2.5} strokeDasharray="6 3" fill="url(#forecastGrad)" dot={{ r: 5, fill: '#FFFFFF', stroke: '#DC2626', strokeWidth: 2.5 }} activeDot={{ r: 7 }} />
            <Area type="monotone" dataKey="upper" name="Upper Bound" stroke="#CBD5E1" strokeWidth={1} fill="none" dot={false} />
            <Area type="monotone" dataKey="lower" name="Lower Bound" stroke="#CBD5E1" strokeWidth={1} fill="none" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-border">
        {[
          { label: 'Apr Forecast', value: '₱5.15M', change: '+6.9%', up: true },
          { label: 'Q2 Total', value: '₱16.55M', change: '+13.2%', up: true },
          { label: 'Peak Month', value: 'August', change: '₱6.78M', up: true },
        ]?.map((s, i) => (
          <div key={i} className="text-center">
            <p className="font-data text-sm md:text-base font-semibold" style={{ color: '#0F172A' }}>{s?.value}</p>
            <p className="font-caption text-xs" style={{ color: '#475569' }}>{s?.label}</p>
            <p className={`font-caption text-xs font-medium mt-0.5 ${s?.up ? 'text-emerald-600' : 'text-red-500'}`}>{s?.change}</p>
          </div>
        ))}
      </div>
    </div>
  );
}